import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Analytics } from '@mui/icons-material';
import { enhancedApi as adminApi } from '@store/generated/admin';
import type { AdminTelemetrySessionListItem } from '@store/generated/admin';
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { FilterableDataTable } from '../components/filterable-data-table';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

const { useGetV2AdminEncountersTelemetrySessionsQuery } = adminApi;

export default function EncounterTelemetrySessionsList() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('per_page') || '20', 10);
  const sortBy = searchParams.get('sort_by') || 'created_at';
  const sortOrder = (searchParams.get('sort_order') || 'desc') as 'asc' | 'desc';
  const filters: {
    organizationId?: string;
    userId?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {
    organizationId: searchParams.get('organization_id') || undefined,
    userId: searchParams.get('user_id') || undefined,
    dateFrom: searchParams.get('date_from') || undefined,
    dateTo: searchParams.get('date_to') || undefined,
  };

  // Fetch sessions data
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useGetV2AdminEncountersTelemetrySessionsQuery({
    page,
    perPage,
    sortBy: sortBy || 'created_at',
    sortOrder: sortOrder || 'desc',
    ...(filters.organizationId && { organizationId: filters.organizationId }),
    ...(filters.userId && { userId: filters.userId }),
    ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
    ...(filters.dateTo && { dateTo: filters.dateTo }),
  });

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, string>) => {
      // Check if filters actually changed from URL params
      const currentOrgId = searchParams.get('organization_id') || '';
      const currentUserId = searchParams.get('user_id') || '';
      const currentDateFrom = searchParams.get('date_from') || '';
      const currentDateTo = searchParams.get('date_to') || '';
      
      const newOrgId = newFilters.organization_id || '';
      const newUserId = newFilters.user_id || '';
      const newDateFrom = newFilters.date_from || '';
      const newDateTo = newFilters.date_to || '';
      
      const filtersChanged =
        currentOrgId !== newOrgId ||
        currentUserId !== newUserId ||
        currentDateFrom !== newDateFrom ||
        currentDateTo !== newDateTo;
      
      // If filters haven't changed, don't update URL (prevents reset on mount)
      if (!filtersChanged) {
        return;
      }
      
      const params = new URLSearchParams(searchParams);
      params.set('page', '1'); // Reset to first page on filter change

      // Preserve existing sort and per_page params
      if (sortBy) params.set('sort_by', sortBy);
      if (sortOrder) params.set('sort_order', sortOrder);
      if (perPage) params.set('per_page', perPage.toString());

      // Update filter params
      if (newFilters.organization_id) {
        params.set('organization_id', newFilters.organization_id);
      } else {
        params.delete('organization_id');
      }
      if (newFilters.user_id) {
        params.set('user_id', newFilters.user_id);
      } else {
        params.delete('user_id');
      }
      if (newFilters.date_from) {
        params.set('date_from', newFilters.date_from);
      } else {
        params.delete('date_from');
      }
      if (newFilters.date_to) {
        params.set('date_to', newFilters.date_to);
      } else {
        params.delete('date_to');
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams, sortBy, sortOrder, perPage],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('per_page', newPerPage.toString());
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
      const params = new URLSearchParams(searchParams);
      params.set('sort_by', newSortBy);
      params.set('sort_order', newSortOrder);
      params.set('page', '1'); // Reset to first page on sort change
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const getRowUrl = useCallback((row: AdminTelemetrySessionListItem) => {
    return `/admin-v2/encounter/${row.encounter_id}`;
  }, []);

  // Helper function to format summary stats
  const formatSummaryStats = (summary: AdminTelemetrySessionListItem['summary']) => {
    if (!summary || summary.total_events === 0) {
      return 'No events';
    }

    const stats: Array<{ label: string; value: string; color?: 'default' | 'primary' | 'success' | 'warning' }> = [];

    // Primary metric: total events
    stats.push({
      label: 'Events',
      value: `${summary.total_events}`,
      color: 'primary',
    });

    // Copy rate (very interesting metric)
    if (summary.copy_rate !== null && summary.copy_rate !== undefined && summary.total_selections) {
      const copyRatePercent = (summary.copy_rate * 100).toFixed(0);
      stats.push({
        label: 'Copy Rate',
        value: `${copyRatePercent}%`,
        color: copyRatePercent >= '30' ? 'success' : 'default',
      });
    }

    // Unique sections interacted (shows engagement breadth)
    if (summary.unique_sections_interacted) {
      stats.push({
        label: 'Sections',
        value: `${summary.unique_sections_interacted}`,
      });
    }

    // Total char delta (shows edit activity)
    if (summary.total_char_delta !== undefined && summary.total_char_delta !== null) {
      const delta = summary.total_char_delta;
      const sign = delta >= 0 ? '+' : '';
      stats.push({
        label: 'Char Δ',
        value: `${sign}${delta.toLocaleString()}`,
        color: delta > 0 ? 'success' : delta < 0 ? 'warning' : 'default',
      });
    }

    return stats;
  };

  const columns = useMemo(
    () => [
      {
        key: 'encounter_id' as keyof AdminTelemetrySessionListItem,
        label: 'Encounter ID',
        sortable: true,
        render: (_value: unknown, row: AdminTelemetrySessionListItem) => (
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
              {row.encounter_id.substring(0, 8)}...
            </Typography>
            {row.note_id && (
              <Typography variant="caption" color="text.secondary">
                Note: {row.note_id.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'user_id' as keyof AdminTelemetrySessionListItem,
        label: 'User',
        sortable: true,
        render: (_value: unknown, row: AdminTelemetrySessionListItem) => (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {row.user_name || `User ${row.user_id.substring(0, 8)}...`}
            </Typography>
            {row.user_email && (
              <Typography variant="caption" color="text.secondary">
                {row.user_email}
              </Typography>
            )}
            {!row.user_name && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {row.user_id.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'organization_id' as keyof AdminTelemetrySessionListItem,
        label: 'Organization',
        sortable: true,
        render: (_value: unknown, row: AdminTelemetrySessionListItem) => {
          if (!row.organization_id && !row.organization_name) {
            return <Chip label="None" size="small" color="default" variant="outlined" />;
          }
          return (
            <Box>
              {row.organization_name && (
                <Typography variant="body2" fontWeight="medium">
                  {row.organization_name}
                </Typography>
              )}
              {row.organization_id && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {row.organization_id.substring(0, 8)}...
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        key: 'summary' as keyof AdminTelemetrySessionListItem,
        label: 'Summary',
        render: (_value: unknown, row: AdminTelemetrySessionListItem) => {
          const stats = formatSummaryStats(row.summary);
          const isStatsArray = Array.isArray(stats);

          return (
            <Box>
              {isStatsArray ? (
                <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                  {stats.map((stat, idx) => (
                    <Chip
                      key={idx}
                      label={`${stat.label}: ${stat.value}`}
                      size="small"
                      color={stat.color || 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" fontSize="0.875rem">
                  {stats}
                </Typography>
              )}
              {row.summary.session_duration_seconds !== null &&
                row.summary.session_duration_seconds !== undefined && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Duration:{' '}
                    {(() => {
                      const seconds = Math.round(row.summary.session_duration_seconds);
                      if (seconds >= 60) {
                        const minutes = Math.floor(seconds / 60);
                        const remainingSeconds = seconds % 60;
                        if (remainingSeconds === 0) {
                          return `${minutes}m`;
                        }
                        return `${minutes}m ${remainingSeconds}s`;
                      }
                      return `${seconds}s`;
                    })()}
                    {row.summary.event_frequency_per_minute !== null &&
                      row.summary.event_frequency_per_minute !== undefined && (
                        <> • {row.summary.event_frequency_per_minute.toFixed(2)} events/min</>
                      )}
                  </Typography>
                )}
            </Box>
          );
        },
      },
      {
        key: 'created_at' as keyof AdminTelemetrySessionListItem,
        label: 'Created',
        sortable: true,
        render: (value: unknown) => (
          <Box>
            <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(value as string)}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'updated_at' as keyof AdminTelemetrySessionListItem,
        label: 'Updated',
        sortable: true,
        render: (value: unknown) => (
          <Box>
            <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(value as string)}
            </Typography>
          </Box>
        ),
      },
    ],
    [],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: 'organization_id',
        label: 'Organization ID',
        type: 'text' as const,
      },
      {
        key: 'user_id',
        label: 'User ID',
        type: 'text' as const,
      },
      {
        key: 'date_from',
        label: 'Date From',
        type: 'text' as const,
      },
      {
        key: 'date_to',
        label: 'Date To',
        type: 'text' as const,
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Analytics fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Encounter Telemetry Sessions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse all encounter telemetry sessions
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Sessions Table */}
        {error ? (
          <Box textAlign="center" py={8}>
            <Typography color="error" variant="h6" mb={2}>
              Failed to load sessions
            </Typography>
            <Button onClick={() => void refetch()} variant="outlined">
              Retry
            </Button>
          </Box>
        ) : (
          <FilterableDataTable<AdminTelemetrySessionListItem>
            title="Telemetry Sessions"
            columns={columns}
            data={sessionsData?.items || []}
            total={sessionsData?.total}
            loading={isLoading}
            page={page}
            perPage={perPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filterConfigs}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onFiltersChange={handleFiltersChange}
            onSortChange={handleSortChange}
            getRowUrl={getRowUrl}
            emptyMessage={
              Object.values(filters).some(v => v !== undefined)
                ? 'No sessions match the current filters'
                : 'No sessions found'
            }
          />
        )}
      </main>
    </div>
  );
}

